<mat-card>
  <div class="page-header mat-h1">
    Retailer Phone Numbers Insights
    <div class="subtitle mat-subtitle-1">
      Search, filter, and analyze retailer phone numbers with technical precision.
    </div>
  </div>
  <mat-accordion>
    <mat-expansion-panel expanded>
      <mat-expansion-panel-header>
        <mat-panel-title>Filters</mat-panel-title>
        <mat-panel-description>Refine your search</mat-panel-description>
      </mat-expansion-panel-header>
      <form [formGroup]="filterForm" class="filter-form" (ngSubmit)="search()">
        <div class="filter-grid">
          <mat-form-field appearance="fill">
            <mat-label>Program</mat-label>
            <mat-select formControlName="programId" matTooltip="Filter by Program">
              <mat-option [value]="">Any</mat-option>
              @for (program of programs; track program) {
                <mat-option [value]="program.programId">
                  {{ program.name }}
                </mat-option>
              }
            </mat-select>
          </mat-form-field>
          <mat-form-field appearance="fill">
            <mat-label>Active</mat-label>
            <mat-select formControlName="active" matTooltip="Show only active/inactive numbers">
              <mat-option [value]="">Any</mat-option>
              <mat-option [value]="true">Active</mat-option>
              <mat-option [value]="false">Inactive</mat-option>
            </mat-select>
          </mat-form-field>
          <mat-form-field appearance="fill">
            <mat-label>Retailer</mat-label>
            <input type="text" matInput [formControl]="retailerSearchControl" [matAutocomplete]="autoRetailer" placeholder="Search retailer by name or code">
            <mat-autocomplete #autoRetailer="matAutocomplete" [displayWith]="displayRetailerFn" (optionSelected)="onRetailerSelected($event.option.value)">
              @for (retailer of filteredRetailers | async; track retailer) {
                <mat-option [value]="retailer">
                  {{ retailer.name }} @if (retailer.retailerCode) {
                  <span>({{ retailer.retailerCode }})</span>
                }
              </mat-option>
            }
          </mat-autocomplete>
        </mat-form-field>
        <mat-form-field appearance="fill">
          <mat-label>Call Center Phone Number ID</mat-label>
          <input matInput formControlName="callCenterPhoneNumberId" type="number" matTooltip="Filter by Call Center Phone Number ID">
        </mat-form-field>
        <mat-form-field appearance="fill">
          <mat-label>Created From</mat-label>
          <input matInput formControlName="createdFrom" [matDatepicker]="pickerFrom" matTooltip="Show numbers created after this date">
          <mat-datepicker-toggle matSuffix [for]="pickerFrom"></mat-datepicker-toggle>
          <mat-datepicker #pickerFrom></mat-datepicker>
        </mat-form-field>
        <mat-form-field appearance="fill">
          <mat-label>Created To</mat-label>
          <input matInput formControlName="createdTo" [matDatepicker]="pickerTo" matTooltip="Show numbers created before this date">
          <mat-datepicker-toggle matSuffix [for]="pickerTo"></mat-datepicker-toggle>
          <mat-datepicker #pickerTo></mat-datepicker>
        </mat-form-field>
      </div>
      <div class="filter-actions">
        <button mat-raised-button color="primary" type="submit">
          <mat-icon>search</mat-icon> Search
        </button>
        <button mat-stroked-button type="button" (click)="clearFilters()">
          <mat-icon>refresh</mat-icon> Reset All
        </button>
      </div>
    </form>
  </mat-expansion-panel>
</mat-accordion>
</mat-card>

<mat-card>
  @if (loading) {
    <div class="loading-skeleton">
      <mat-progress-spinner mode="indeterminate"></mat-progress-spinner>
      <div class="skeleton-table"></div>
    </div>
  }
  @if (error && !loading) {
    <div class="error-message mat-error">
      <mat-icon color="warn">error</mat-icon> {{ errorMessage }}
    </div>
  }
  <div class="table-container" [hidden]="loading || error">
    <table mat-table [dataSource]="dataSource" matSort (matSortChange)="onSortChange($event)" class="mat-elevation-z1 dense-table">
      <ng-container matColumnDef="retailerName">
        <th mat-header-cell *matHeaderCellDef mat-sort-header="retailerName" class="sticky-header">Retailer</th>
        <td mat-cell *matCellDef="let row">{{ row.retailerName }}</td>
      </ng-container>
      <ng-container matColumnDef="programName">
        <th mat-header-cell *matHeaderCellDef mat-sort-header="programName" class="sticky-header">Program Name</th>
        <td mat-cell *matCellDef="let row">{{ row.programName }}</td>
      </ng-container>
      <ng-container matColumnDef="callCenterPhoneNumberPhoneNumber">
        <th mat-header-cell *matHeaderCellDef mat-sort-header="callCenterPhoneNumberPhoneNumber" class="sticky-header">Phone Number</th>
        <td mat-cell *matCellDef="let row">{{ row.callCenterPhoneNumberPhoneNumber }}</td>
      </ng-container>
      <ng-container matColumnDef="active">
        <th mat-header-cell *matHeaderCellDef mat-sort-header="active" class="sticky-header">Active</th>
        <td mat-cell *matCellDef="let row">
          <mat-chip [color]="row.active ? 'primary' : 'warn'" selected>
            {{ row.active ? 'Yes' : 'No' }}
          </mat-chip>
        </td>
      </ng-container>
      <ng-container matColumnDef="created">
        <th mat-header-cell *matHeaderCellDef mat-sort-header="created" class="sticky-header">Created</th>
        <td mat-cell *matCellDef="let row">{{ row.created | date:'short' }}</td>
      </ng-container>
      <ng-container matColumnDef="createdBy">
        <th mat-header-cell *matHeaderCellDef class="sticky-header">Created By</th>
        <td mat-cell *matCellDef="let row">{{ row.createdBy }}</td>
      </ng-container>
      <ng-container matColumnDef="lastModified">
        <th mat-header-cell *matHeaderCellDef mat-sort-header="lastModified" class="sticky-header">Last Modified</th>
        <td mat-cell *matCellDef="let row">{{ row.lastModified | date:'short' }}</td>
      </ng-container>
      <ng-container matColumnDef="lastModifiedBy">
        <th mat-header-cell *matHeaderCellDef class="sticky-header">Last Modified By</th>
        <td mat-cell *matCellDef="let row">{{ row.lastModifiedBy }}</td>
      </ng-container>
      <tr mat-header-row *matHeaderRowDef="displayedColumns; sticky: true"></tr>
      <tr mat-row *matRowDef="let row; columns: displayedColumns;"></tr>
    </table>
  </div>
  <mat-paginator [length]="totalRecords" [pageSize]="filterForm.value.pageSize" [pageSizeOptions]="[10, 25, 50, 100]" (page)="onPaginateChange()"></mat-paginator>
</mat-card>
